import { useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { InvestorProfileType, InvestmentCountOption } from '@inversor-hub/types';
import { useCompleteOnboarding } from '@/lib/api/me';
import StepProfileType from './StepProfileType';
import StepHasInvestmentsCount from './StepHasInvestmentsCount';
import StepWantsToStart from './StepWantsToStart';

type Step = 'profile_type' | 'has_investments_count' | 'wants_to_start_info';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export default function OnboardingInvestorModal({
  open,
  onClose,
  onComplete,
}: OnboardingModalProps) {
  const [step, setStep] = useState<Step>('profile_type');
  const [profileType, setProfileType] = useState<InvestorProfileType | null>(null);
  const [investmentCount, setInvestmentCount] = useState<InvestmentCountOption | null>(null);
  const navigate = useNavigate();
  const { mutate: completeOnboarding, isPending } = useCompleteOnboarding();

  const callApiAndRedirect = (
    profile: InvestorProfileType,
    count: InvestmentCountOption | undefined,
    path: string
  ) => {
    completeOnboarding(
      { investorProfile: profile, investmentCount: count },
      {
        onSuccess: () => {
          onComplete();
          navigate(path);
        },
      }
    );
  };

  const handleContinue = () => {
    if (!profileType) return;

    if (profileType === 'has_investments') {
      setStep('has_investments_count');
    } else if (profileType === 'wants_to_start') {
      setStep('wants_to_start_info');
    } else {
      callApiAndRedirect('exploring', undefined, '/dashboard');
    }
  };

  const handleRegister = () => {
    callApiAndRedirect('has_investments', investmentCount ?? undefined, '/inversiones/nueva');
  };

  const handleExplore = () => {
    callApiAndRedirect('has_investments', investmentCount ?? undefined, '/dashboard');
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, position: 'relative' } }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: 4 }}>
        {step === 'profile_type' && (
          <StepProfileType
            selected={profileType}
            onSelect={setProfileType}
            onContinue={handleContinue}
          />
        )}

        {step === 'has_investments_count' && (
          <StepHasInvestmentsCount
            selected={investmentCount}
            onSelect={setInvestmentCount}
            onRegister={handleRegister}
            onExplore={handleExplore}
            isPending={isPending}
          />
        )}

        {step === 'wants_to_start_info' && (
          <StepWantsToStart
            onGoToOpportunities={() =>
              callApiAndRedirect('wants_to_start', undefined, '/oportunidades')
            }
            onExplore={() => callApiAndRedirect('wants_to_start', undefined, '/dashboard')}
            isPending={isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
